import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { KeyRound, Plus, Trash2, X, ShieldCheck, Pencil } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../lib/auth';

const PERM_KEYS = [
  { key: 'courses', label: 'Manage Courses' },
  { key: 'categories', label: 'Manage Categories' },
  { key: 'vouchers', label: 'Manage Vouchers' },
  { key: 'testimonials', label: 'Manage Testimonials' },
  { key: 'users', label: 'View Users' },
  { key: 'enrollments', label: 'View Enrollments' },
  { key: 'contact', label: 'Manage Enquiries' },
  { key: 'settings', label: 'Update Site Settings' },
  { key: 'admins', label: 'View Admins list' },
];

export default function AdminAdmins() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [passwordForm, setPasswordForm] = useState(null);
  const { isSuperAdmin } = useAuth();
  const { toast } = useToast();

  const load = () => api.admin.admins().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!isSuperAdmin) {
    return <div className="bg-white p-10 rounded-2xl border border-gray-200 text-center">
      <ShieldCheck size={40} className="mx-auto text-[#6b47dc] mb-3" />
      <h2 className="font-bold text-lg">Super admin only</h2>
      <p className="text-gray-500 text-sm mt-1">Only the super admin can manage admin accounts.</p>
    </div>;
  }

  const openNew = () => setForm({ name: '', email: '', password: '', permissions: {} });
  const openEdit = (r) => setForm({ id: r.id, name: r.name, email: r.email, password: '', permissions: r.permissions || {} });
  const openPasswordReset = (r) => setPasswordForm({ id: r.id, name: r.name, email: r.email, password: '' });

  const togglePerm = (k) => setForm(f => ({ ...f, permissions: { ...f.permissions, [k]: !f.permissions[k] } }));

  const save = async () => {
    try {
      if (form.id) {
        const payload = { name: form.name, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.admin.adminUpdate(form.id, payload);
      } else {
        if (!form.password) { toast({ title: 'Password required', variant: 'destructive' }); return; }
        await api.admin.adminCreate({ name: form.name, email: form.email, password: form.password, permissions: form.permissions });
      }
      toast({ title: 'Saved' });
      setForm(null); load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this admin?')) return;
    try {
      await api.admin.adminDelete(id);
      toast({ title: 'Removed' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const resetPassword = async () => {
    if (!passwordForm.password || passwordForm.password.length < 8) {
      toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' });
      return;
    }
    try {
      await api.admin.adminResetPassword(passwordForm.id, passwordForm.password);
      toast({ title: 'Password reset' });
      setPasswordForm(null);
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl md:text-3xl font-extrabold">Admins</h1><p className="text-gray-500 text-sm mt-1">{rows.length} admin accounts</p></div>
        <Button onClick={openNew} className="bg-[#6b47dc] hover:bg-[#5a3ac0]"><Plus size={16} className="mr-1" /> New admin</Button>
      </div>

      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.id} className="bg-white p-5 rounded-2xl border border-gray-200">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6b47dc] to-[#ff5722] grid place-items-center text-white font-extrabold">{r.name[0]?.toUpperCase()}</div>
                <div>
                  <div className="font-bold">{r.name}</div>
                  <div className="text-xs text-gray-500">{r.email}</div>
                  <span className={`mt-1 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${r.role === 'super_admin' ? 'bg-gradient-to-r from-[#6b47dc] to-[#ff5722] text-white' : 'bg-purple-100 text-[#6b47dc]'}`}>{r.role.replace('_', ' ')}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button title="Reset password" onClick={() => openPasswordReset(r)} className="p-1.5 text-[#6b47dc] hover:bg-purple-50 rounded"><KeyRound size={14} /></button>
                {r.role !== 'super_admin' && (
                  <>
                    <button title="Edit admin" onClick={() => openEdit(r)} className="p-1.5 text-[#6b47dc] hover:bg-purple-50 rounded"><Pencil size={14} /></button>
                    <button title="Remove admin" onClick={() => remove(r.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            </div>
            {r.role !== 'super_admin' && (
              <div className="flex flex-wrap gap-1.5">
                {PERM_KEYS.filter(p => r.permissions?.[p.key]).map(p => (
                  <span key={p.key} className="text-[10px] font-semibold px-2 py-0.5 rounded bg-purple-50 text-[#6b47dc]">{p.label}</span>
                ))}
                {Object.values(r.permissions || {}).filter(Boolean).length === 0 && <span className="text-xs text-gray-400">No permissions assigned</span>}
              </div>
            )}
            {r.role === 'super_admin' && <div className="text-xs text-gray-500">Has full access to all features (immutable).</div>}
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-extrabold text-lg">{form.id ? 'Edit admin' : 'New admin'}</h3>
              <button onClick={() => setForm(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="text-xs font-bold text-gray-500">Full name</label><input className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Email</label><input type="email" disabled={!!form.id} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc] disabled:bg-gray-50" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="text-xs font-bold text-gray-500">Password {form.id && <span className="text-gray-400">(leave empty to keep unchanged)</span>}</label><input type="password" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>

              <div>
                <label className="text-xs font-bold text-gray-500 mb-2 block">Permissions</label>
                <div className="grid grid-cols-2 gap-2">
                  {PERM_KEYS.map(p => (
                    <label key={p.key} className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 hover:border-purple-200 cursor-pointer text-sm">
                      <input type="checkbox" className="accent-[#6b47dc]" checked={!!form.permissions[p.key]} onChange={() => togglePerm(p.key)} />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white p-5 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button onClick={save} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">Save</Button>
            </div>
          </div>
        </div>
      )}

      {passwordForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPasswordForm(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-extrabold text-lg">Reset admin password</h3>
                <p className="text-xs text-gray-500 mt-1">{passwordForm.name} · {passwordForm.email}</p>
              </div>
              <button onClick={() => setPasswordForm(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-500">New password</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]"
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500">Minimum 8 characters. The existing password will stop working immediately.</p>
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPasswordForm(null)}>Cancel</Button>
              <Button onClick={resetPassword} className="bg-[#6b47dc] hover:bg-[#5a3ac0]">Reset password</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
